/**
 * UOC 转出排队 — admin queue process / reject / confirm-split
 */
import { useState } from 'react';
import { Button, Input, Modal, Select, Space, Tag, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface TransferRow {
  id: number;
  from_user_id: number;
  to_user_id: number;
  amount_uoc: string;
  status: string;
  split_status: string;
  memo: string | null;
  create_time: string;
  from_user?: { username: string };
  to_user?: { username: string };
}

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  queued: { color: 'orange', text: '排队中' },
  completed: { color: 'green', text: '已完成' },
  rejected: { color: 'red', text: '已拒绝' },
  cancelled: { color: 'default', text: '已取消' },
};

export function UocTransferQueuePage() {
  const [status, setStatus] = useState<string | undefined>('queued');
  const [rejectTarget, setRejectTarget] = useState<TransferRow | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const hasNode = useAuthStore((s) => s.hasNode);
  const qc = useQueryClient();

  const canApprove = hasNode('platform.withdraw.approve');

  async function processRow(row: TransferRow) {
    try {
      await api.post(`/api/admin/uoc-transfers/${row.id}/process`, {});
      message.success(`已处理 #${row.id}`);
      qc.invalidateQueries({ queryKey: ['admin', 'uoc-transfers'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '处理失败');
    }
  }

  async function confirmSplit(row: TransferRow) {
    try {
      await api.post(`/api/admin/uoc-transfers/${row.id}/confirm-split`, {});
      message.success(`已确认拆分 #${row.id}`);
      qc.invalidateQueries({ queryKey: ['admin', 'uoc-transfers'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拆分失败');
    }
  }

  async function reject() {
    if (!rejectTarget) return;
    try {
      await api.post(`/api/admin/uoc-transfers/${rejectTarget.id}/reject`, {
        note: rejectNote,
      });
      message.success(`已拒绝 #${rejectTarget.id}`);
      setRejectTarget(null);
      setRejectNote('');
      qc.invalidateQueries({ queryKey: ['admin', 'uoc-transfers'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '拒绝失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '转出方',
      width: 120,
      render: (_: unknown, r: TransferRow) =>
        r.from_user?.username ?? `#${r.from_user_id}`,
    },
    {
      title: '接收方',
      width: 120,
      render: (_: unknown, r: TransferRow) => r.to_user?.username ?? `#${r.to_user_id}`,
    },
    {
      title: 'UOC',
      dataIndex: 'amount_uoc',
      width: 140,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: string) => {
        const t = STATUS_TAG[v] ?? { color: 'default', text: v };
        return <Tag color={t.color}>{t.text}</Tag>;
      },
    },
    { title: '拆分', dataIndex: 'split_status', width: 90 },
    { title: '时间', dataIndex: 'create_time', width: 170 },
    {
      title: '操作',
      width: 220,
      render: (_: unknown, row: TransferRow) =>
        canApprove ? (
          <Space size="small">
            {row.status === 'queued' && (
              <>
                <Button size="small" type="link" onClick={() => processRow(row)}>
                  处理
                </Button>
                <Button size="small" type="link" danger onClick={() => setRejectTarget(row)}>
                  拒绝
                </Button>
              </>
            )}
            {row.status === 'completed' && row.split_status !== 'confirmed' && (
              <Button size="small" type="link" onClick={() => confirmSplit(row)}>
                确认拆分
              </Button>
            )}
          </Space>
        ) : null,
    },
  ];

  return (
    <div>
      <Typography.Title level={4}>UOC 转出排队</Typography.Title>
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="状态"
          style={{ width: 140 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'queued', label: '排队中' },
            { value: 'completed', label: '已完成' },
            { value: 'rejected', label: '已拒绝' },
          ]}
        />
      </Space>
      <DataTable<TransferRow>
        queryKey={['admin', 'uoc-transfers', status ?? 'all']}
        endpoint="/api/admin/uoc-transfers"
        extraParams={status ? { status } : {}}
        columns={columns}
      />
      <Modal
        title="拒绝 UOC 转出"
        open={!!rejectTarget}
        onOk={reject}
        onCancel={() => setRejectTarget(null)}
      >
        <Input.TextArea
          rows={3}
          placeholder="拒绝原因"
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
        />
      </Modal>
    </div>
  );
}
